import { Image, X } from '@/lib/icons'
import { formatNumber } from '@ayub/shared'
import { cn } from '@/lib/utils'

export interface MessageComposerProps {
  textUz: string
  onTextUzChange: (value: string) => void
  textRu: string
  onTextRuChange: (value: string) => void
  fileId: string
  onFileIdChange: (value: string) => void
  fileType: 'photo' | 'video'
  onFileTypeChange: (value: 'photo' | 'video') => void
  /** Media borligiga qarab 1024 yoki 4096 — `AudienceSection`dagi kabi ota komponentda hisoblanadi. */
  charLimit: number
}

/**
 * "Xabar matni" + "Media (ixtiyoriy)" — `docs/03-app-flow.md` Sahifa 5.
 *
 * ⚠️ QAROR (`fileId` manbasi): WebApp'da hech qanday fayl YUKLASH oqimi yo'q — butun
 * loyiha bo'ylab qidiruv (`grep fileId`) shuni ko'rsatdi: `fileId` faqat SERVER tarafida,
 * Telegram bot xabarlaridan (`services/notify.ts`, `sendPhoto`/`sendVideo`) ishlatiladi,
 * WebApp'da uni "olish" uchun mavjud komponent/endpoint YO'Q. Yangi yuklash infratuzilmasi
 * qurish (fayl serverga yuklab, Telegram'ga forward qilib, `file_id` qaytarish) bu
 * vazifaning ko'lamidan ANCHA katta va yangi backend endpoint talab qilardi (vazifa
 * qoidasi: `api.ts`ga tegilmaydi, yangi server ishi bu bosqichda yo'q). Shuning uchun eng
 * sodda, halol yechim tanlandi: admin `fileId`ni QO'LDA kiritadi (masalan botga rasmni
 * o'zi yuborib, botdan/loglardan ID'ni nusxalab oladi) va turini (rasm/video) tanlaydi.
 * Bu — chekka holat emas, ATAYLAB shunday: maydon ixtiyoriy va bo'sh qoldirilsa oddiy
 * matnli xabar yuboriladi.
 */
export function MessageComposer({
  textUz,
  onTextUzChange,
  textRu,
  onTextRuChange,
  fileId,
  onFileIdChange,
  fileType,
  onFileTypeChange,
  charLimit,
}: MessageComposerProps) {
  return (
    <section className="flex flex-col gap-4 rounded-card border border-app-border bg-app-card p-4">
      <h2 className="text-sm font-semibold text-app-text">Xabar matni</h2>

      <TextareaField
        id="broadcast-text-uz"
        label="O'zbekcha"
        value={textUz}
        onChange={onTextUzChange}
        limit={charLimit}
      />
      <TextareaField
        id="broadcast-text-ru"
        label="Ruscha"
        value={textRu}
        onChange={onTextRuChange}
        limit={charLimit}
      />

      <div className="flex flex-col gap-2 border-t border-app-border pt-3">
        <label
          htmlFor="broadcast-file-id"
          className="inline-flex items-center gap-1.5 text-xs font-medium text-app-hint"
        >
          <Image size={14} aria-hidden="true" />
          Rasm/video qo&apos;shish (ixtiyoriy) — Telegram <code>file_id</code>
        </label>
        <div className="flex flex-wrap items-center gap-2">
          <input
            id="broadcast-file-id"
            type="text"
            value={fileId}
            onChange={(event) => onFileIdChange(event.target.value)}
            placeholder="Masalan: AgACAgIAAxkBAAI..."
            className="h-11 min-w-0 flex-1 rounded-input border border-app-border bg-app-bg px-3 text-base text-app-text placeholder:text-app-hint outline-none focus-visible:border-brand"
          />
          {fileId.length > 0 && (
            <button
              type="button"
              onClick={() => onFileIdChange('')}
              aria-label="Mediani olib tashlash"
              className="flex size-11 shrink-0 items-center justify-center rounded-button text-app-hint hover:bg-app-bg"
            >
              <X size={16} aria-hidden="true" />
            </button>
          )}
        </div>
        {fileId.length > 0 && (
          <div role="radiogroup" aria-label="Media turi" className="flex gap-2">
            <MediaTypeOption
              label="Rasm"
              active={fileType === 'photo'}
              onSelect={() => onFileTypeChange('photo')}
            />
            <MediaTypeOption
              label="Video"
              active={fileType === 'video'}
              onSelect={() => onFileTypeChange('video')}
            />
          </div>
        )}
      </div>
    </section>
  )
}

function TextareaField({
  id,
  label,
  value,
  onChange,
  limit,
}: {
  id: string
  label: string
  value: string
  onChange: (value: string) => void
  limit: number
}) {
  // Telegram belgi chegarasi TRIM QILINGAN matnga nisbatan tekshiriladi
  // (`services/broadcast.ts`: `input.textUz.trim()` limitga solishtirilishidan OLDIN),
  // shuning uchun hisoblagich ham shu bilan mos bo'lishi uchun trim qilingan uzunlikni
  // ko'rsatadi — aks holda admin "999/1024" ko'rib yuborsa, serverda boshqa (masalan bo'sh
  // joylar tufayli kattaroq) son bilan 422 olishi mumkin edi.
  const length = value.trim().length
  const over = length > limit

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between">
        <label htmlFor={id} className="text-xs font-medium text-app-hint">
          {label}
        </label>
        <span
          className={cn(
            'tabular text-xs',
            over ? 'font-semibold text-urgency-overdue-text' : 'text-app-hint',
          )}
        >
          {formatNumber(length)}/{formatNumber(limit)}
        </span>
      </div>
      <textarea
        id={id}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        rows={4}
        placeholder="Xabar matnini yozing..."
        className={cn(
          'resize-y rounded-input border bg-app-bg px-3 py-2 text-base text-app-text outline-none',
          over
            ? 'border-urgency-overdue focus-visible:border-urgency-overdue'
            : 'border-app-border focus-visible:border-brand',
        )}
      />
    </div>
  )
}

function MediaTypeOption({
  label,
  active,
  onSelect,
}: {
  label: string
  active: boolean
  onSelect: () => void
}) {
  return (
    <label
      className={cn(
        'flex h-9 cursor-pointer items-center gap-2 rounded-button border px-3 text-sm transition-colors duration-150 ease-out',
        active
          ? 'border-brand bg-brand-subtle text-brand'
          : 'border-app-border text-app-hint hover:bg-app-bg',
      )}
    >
      <input
        type="radio"
        name="broadcast-media-type"
        checked={active}
        onChange={onSelect}
        className="size-4 accent-brand"
      />
      {label}
    </label>
  )
}
